import { useState, useEffect } from 'react';
import { BuildingLogItem } from '../components/buildingLog/BuildingLog';
import { registryData } from '../config/registryData';
import { getBuildingLogs } from '../services/RegistryService';
import { formatDateTime } from '../../common/utils/Date/Date';
import { compareBuildingLogsForDisplay } from '../utils/registryDataProcessor';

interface UseLastChecksReturn {
  isLoading: boolean;
  lastCheckDates: Record<string, string>;
  lastBuildingLogs: Record<string, BuildingLogItem[]>;
  refresh: () => Promise<void>;
}

export const useLastChecks = (): UseLastChecksReturn => {
  const [isLoading, setIsLoading] = useState(true);
  const [lastCheckDates, setLastCheckDates] = useState<Record<string, string>>({});
  const [lastBuildingLogs, setLastBuildingLogs] = useState<Record<string, BuildingLogItem[]>>({});

  const fetchLastRegistryChecks = async () => {
    try {
      setIsLoading(true);
      
      // Pro poslední kontroly načteme jen posledních 100 záznamů (mělo by pokrýt poslední týden)
      const recentLogs = await getBuildingLogs(100, 0);
      
      const lastChecks: Record<string, string> = {};
      const lastLogs: Record<string, BuildingLogItem[]> = {};
      
      registryData.forEach((registry) => {
        const logsForType = recentLogs.filter(log => log.typeGroup === registry.type);
        if (logsForType.length > 0) {
          logsForType.sort((a, b) =>
            new Date(b.recordedAt).getTime() - new Date(a.recordedAt).getTime()
          );
          
          // Najdeme nejnovější kontrolu (podle recordedAt)
          const latestRecordedAt = logsForType[0].recordedAt;
          const latestReference = logsForType[0].reference;
          
          // Najdeme všechny logy ze stejné kontroly (podle reference)
          let logsFromLastCheck: BuildingLogItem[];
          if (latestReference) {
            // Pokud máme reference, použijeme ho jako batch ID
            logsFromLastCheck = logsForType.filter(log => log.reference === latestReference);
          } else {
            // Fallback: použijeme stejný recordedAt
            logsFromLastCheck = logsForType.filter(log => log.recordedAt === latestRecordedAt);
          }
          
          // Stejné řazení jako v "Historie kontrol" — vzestupně dle čísla jednotky
          lastLogs[registry.type] = [...logsFromLastCheck].sort(compareBuildingLogsForDisplay);
          lastChecks[registry.type] = formatDateTime(new Date(latestRecordedAt));
        } else {
          // Klíč nechat chybět — RegistryLastCheck si fallback text doplní sám
          // a podle chybějícího data pozná stav "Naplánováno".
          lastLogs[registry.type] = [];
        }
      });
      
      setLastCheckDates(lastChecks);
      setLastBuildingLogs(lastLogs);
    } catch (error) {
      console.error('Error fetching last registry checks:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLastRegistryChecks();
  }, []);

  return {
    isLoading,
    lastCheckDates,
    lastBuildingLogs,
    refresh: fetchLastRegistryChecks
  };
};
